import { gql } from '@apollo/client';
import IconTeam from '@apollo/icons/default/IconTeam.svg';
import {
  Avatar,
  Card,
  CardBody,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import {
  ChecksTableWorkflowCell,
  ChecksTableWorkflowCellWorkflowFragment,
} from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/ChecksTableWorkflowCell';
import {
  CheckWorkflowStatusBadge,
  CheckWorkflowStatusBadgeCheckWorkflowFragment,
} from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/CheckWorkflowStatusBadge';
import {
  CheckWorkflowTaskItem,
  CheckWorkflowTaskItemFragment,
} from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/checkWorkflowTaskItem/CheckWorkflowTaskItem';
import { shouldDisplayTask } from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/getTaskDisplayStatus';
import {
  SuccessfulCheckWorkflowItem,
  SuccessfulCheckWorkflowItemFragment,
} from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/SuccessfulCheckWorkflowItem';
import {
  TaskStatusIcon,
  TaskStatusIconTaskFragment,
} from '../../checksPage/taskStatusIcon/TaskStatusIcon';

export const ChecksForRevisionCardWorfklowFragment = gql`
  fragment ChecksForRevisionCardWorfklowFragment on CheckWorkflow {
    id
    ...CheckWorkflowStatusBadgeCheckWorkflowFragment
    ...ChecksTableWorkflowCellWorkflowFragment
    ...SuccessfulCheckWorkflowItemFragment
    tasks {
      id
      ...CheckWorkflowTaskItemFragment
      ...TaskStatusIconTaskFragment
    }
    triggeredBy {
      id
      ... on User {
        fullName
        avatarUrl
      }
    }
  }
  ${CheckWorkflowStatusBadgeCheckWorkflowFragment}
  ${CheckWorkflowTaskItemFragment}
  ${SuccessfulCheckWorkflowItemFragment}
  ${ChecksTableWorkflowCellWorkflowFragment}
  ${TaskStatusIconTaskFragment}
`;

export const ChecksForRevisionCard = ({
  shouldHighlightSubgraphs,
  checkWorkflow,
}: {
  shouldHighlightSubgraphs: boolean;
  checkWorkflow: GraphQLTypes.ChecksForRevisionCardWorfklowFragment;
}) => {
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );

  const thresholdCrossed: number | null | undefined = (
    checkWorkflow.tasks.find(
      (task) => task.__typename === 'OperationsCheckTask',
    ) as Extract<
      GraphQLTypes.CheckWorkflowTaskItemFragment,
      { __typename: 'OperationsCheckTask' }
    >
  )?.result?.crossedOperationThreshold;
  const crossedThreshold: boolean =
    thresholdCrossed !== null && thresholdCrossed !== undefined;

  const hasOperationTask =
    checkWorkflow.tasks.findIndex(
      (task) => task.__typename === 'OperationsCheckTask',
    ) > -1;

  const displayedTasks = checkWorkflow.tasks.filter((task) =>
    shouldDisplayTask(task, shouldDescriptionsWarn),
  );

  const user =
    checkWorkflow?.triggeredBy?.__typename === 'User'
      ? checkWorkflow.triggeredBy
      : undefined;

  return (
    <Card>
      <CardBody>
        <TableContainer>
          <Table className="table-fixed">
            <Thead>
              <Tr>
                <Th className="w-1/3">Subgraph</Th>
                <Th>Status</Th>
                <Th>Tasks</Th>
                <Th>Author</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr
                className={classnames(
                  shouldHighlightSubgraphs && 'bg-highlight',
                )}
              >
                <Td className="text-secondary">
                  <div className="flex flex-col truncate">
                    <ChecksTableWorkflowCell workflow={checkWorkflow} />
                  </div>
                </Td>
                <Td>
                  <CheckWorkflowStatusBadge workflow={checkWorkflow} />
                </Td>
                <Td>
                  <div className="flex items-center truncate">
                    <Tooltip
                      disabled={!crossedThreshold}
                      label={
                        <div>
                          We have detected more than{' '}
                          {format.requests(thresholdCrossed)} unique operations.
                          Any additional operations beyond these have not been
                          checked and may be affected.
                        </div>
                      }
                    >
                      <div className="w-full">
                        {displayedTasks.length === 0 ? (
                          <SuccessfulCheckWorkflowItem
                            workflow={checkWorkflow}
                          />
                        ) : (
                          displayedTasks.map((task) => (
                            <CheckWorkflowTaskItem
                              task={task}
                              key={task.id}
                              crossedThreshold={crossedThreshold}
                            />
                          ))
                        )}

                        {!hasOperationTask && (
                          <div className="mx-2 mt-1 flex items-center">
                            <TaskStatusIcon
                              className="size-4"
                              task={
                                GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED
                              }
                            />
                            <p className="ml-2 text-base text-secondary">
                              Operations checks disabled
                            </p>
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </Td>
                <Td>
                  <div className="flex items-center text-base text-secondary">
                    {user ? (
                      <Avatar
                        size="sm"
                        name={user.fullName}
                        src={user.avatarUrl ?? undefined}
                        className="mr-2"
                      />
                    ) : (
                      <IconTeam className="mr-2 text-icon-primary" />
                    )}
                    <Tooltip label={user?.id}>
                      <span className="truncate">
                        {user?.fullName ?? 'Unknown'}
                      </span>
                    </Tooltip>
                  </div>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
};
